import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Camera, CameraOff, Check, CheckCircle2, Edit3, Flashlight, Loader2, Plus, Printer, RefreshCw, ScanLine, Upload, Warehouse } from '@/components/icons';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PageHeader from '@/components/haccp/PageHeader';
import UnitSelect from '@/components/haccp/UnitSelect';
import AllergenSelector from '@/components/haccp/AllergenSelector';
import ReceiptStatusSelector from '@/components/haccp/ReceiptStatusSelector';
import StatusBadge from '@/components/haccp/StatusBadge';
import EntityBadge from '@/components/haccp/EntityBadge';
import ProductThumbnail from '@/components/haccp/ProductThumbnail';
import LabelPrintDialog from '@/components/haccp/LabelPrintDialog';
import ReceiptEditorSheet, { IngredientSearchSelect } from '@/components/haccp/ReceiptEditorSheet';
import ViewportDialogContent from '@/components/haccp/ViewportDialogContent';
import { createReceiptArticleGroupId } from '@/components/haccp/receiptArticleGroups';
import { toast } from 'sonner';
import { captureDocumentPhoto, getCameraErrorMessage, openCameraStream, prepareDocumentCamera, releaseCameraStream as releaseSharedCameraStream, setCameraTorch, supportsCameraTorch, waitForDocumentCameraReady } from '@/lib/camera';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_CAPTURE_DIMENSION = 2560;
const SUPPORTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);
const MIME_BY_EXTENSION = { '.jpg': 'image/jpeg', '.jpeg': 'image/jpeg', '.png': 'image/png', '.webp': 'image/webp', '.pdf': 'application/pdf' };
const NONE = '__none__';
const VALID_INGREDIENT_TYPES = new Set(['raw', 'semifinished', 'final_product']);

const getFileExtension = (filename = '') => { const i = filename.lastIndexOf('.'); return i >= 0 ? filename.slice(i).toLowerCase() : ''; };
const normalizeKnownFileType = (inputFile) => {
  if (!inputFile || inputFile.type) return inputFile;
  const detectedType = MIME_BY_EXTENSION[getFileExtension(inputFile.name)];
  return detectedType ? new File([inputFile], inputFile.name, { type: detectedType, lastModified: inputFile.lastModified || Date.now() }) : inputFile;
};
const norm = v => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');
const keyNorm = v => norm(v).normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '');
const dateInput = v => v ? String(v).slice(0, 10) : '';
const localDateToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const emptyItem = () => ({ _article_group_id: createReceiptArticleGroupId(), product_code: '', product_name: '', lot_number: '', expiry_date: '', quantity: '', unit: '', ingredient_id: '', article_id: '', article_ingredient_id: '', article_reclassify: false, storage_class: '', receipt_status: '', receipt_reason: '' });
const initialManual = () => ({
  _article_group_id: createReceiptArticleGroupId(), commercial_name: '', ingredient_id: '', product_code: '', supplier_id: NONE, article_id: '', article_ingredient_id: '', article_reclassify: false, storage_class: '',
  lot_number: '', received_date: localDateToday(), expiry_date: '',
  initial_quantity: '', unit: '', document_ref: '', receipt_status: '', receipt_reason: ''
});

const manualToEditorItem = (row = {}) => ({
  _article_group_id: row._article_group_id || '',
  product_name: row.commercial_name || '', product_code: row.product_code || '', ingredient_id: row.ingredient_id || '',
  article_id: row.article_id || '', article_ingredient_id: row.article_ingredient_id || '', article_reclassify: !!row.article_reclassify,
  storage_class: row.storage_class || '', lot_number: row.lot_number || '', expiry_date: row.expiry_date || '',
  quantity: row.initial_quantity ?? '', unit: row.unit || '', receipt_status: row.receipt_status || '', receipt_reason: row.receipt_reason || '',
});

const getItemMissingFields = (item = {}) => {
  const missing = [];
  if (!String(item.product_name || '').trim()) missing.push('Articolo / nome commerciale');
  if (!item.ingredient_id) missing.push('Ingrediente');
  if (!item.storage_class) missing.push('Conservazione');
  if (!(Number(item.quantity) > 0)) missing.push('Quantità');
  if (!item.unit) missing.push('Unità');
  if (!String(item.lot_number || '').trim() && item.internal_lot_confirmed !== true) missing.push('Conferma lotto interno');
  if (!item.expiry_date) missing.push('Scadenza');
  if (!item.receipt_status) missing.push('Esito ricevimento');
  if (item.receipt_status === 'non_compliant' && !String(item.receipt_reason || '').trim()) missing.push('Motivo non conformità ricevimento');
  return missing;
};

export default function OCRPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [creatingLots, setCreatingLots] = useState(false);
  const [result, setResult] = useState(null);
  const [docData, setDocData] = useState({ supplier: '', ddt_number: '', ddt_date: '', received_date: localDateToday() });
  const [items, setItems] = useState([]);
  const [createdLots, setCreatedLots] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [ingredientAllergens, setIngredientAllergens] = useState([]);
  const [articles, setArticles] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [torchBusy, setTorchBusy] = useState(false);
  const [photoCaptureBusy, setPhotoCaptureBusy] = useState(false);
  const [videoDevices, setVideoDevices] = useState([]);
  const [activeDeviceId, setActiveDeviceId] = useState('');
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [newIngredient, setNewIngredient] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [ocrEditorOpen, setOcrEditorOpen] = useState(false);
  const [manual, setManual] = useState(() => initialManual());
  const [manualItems, setManualItems] = useState(() => [initialManual()]);
  const [manualItemIndex, setManualItemIndex] = useState(0);
  const [manualSaving, setManualSaving] = useState(false);
  const [editLot, setEditLot] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [printLot, setPrintLot] = useState(null);
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [allergenEdit, setAllergenEdit] = useState(null);
  const [allergenSaving, setAllergenSaving] = useState(false);

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cameraRequestRef = useRef(0);
  const cameraStartingRef = useRef(false);

  const selectableIngredients = useMemo(() => [...ingredients.filter(i => VALID_INGREDIENT_TYPES.has(i.type))].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'it', { sensitivity: 'base' })), [ingredients]);
  const itemStates = useMemo(() => items.map(item => {
    const missing = getItemMissingFields(item);
    const complete = missing.length === 0;
    return { missing, complete };
  }), [items]);
  const visibleItemIndex = items.length ? Math.min(activeItemIndex, items.length - 1) : 0;
  const currentStep = createdLots.length ? 3 : result ? 2 : 1;
  const steps = [{ n: 1, label: 'Documento' }, { n: 2, label: 'Verifica' }, { n: 3, label: 'Lotti creati' }];

  const resetManualFlow = () => {
    const fresh = initialManual();
    setManual(fresh);
    setManualItems([fresh]);
    setManualItemIndex(0);
  };
  const manualItemMissing = item => {
    const missing = [];
    if (!String(item.commercial_name || '').trim()) missing.push('Articolo / nome commerciale');
    if (!item.ingredient_id) missing.push('Ingrediente');
    if (!item.storage_class) missing.push('Conservazione');
    if (!String(item.lot_number || '').trim()) missing.push('Lotto');
    if (!item.expiry_date) missing.push('Scadenza');
    if (!(Number(item.initial_quantity) > 0)) missing.push('Quantità');
    if (!item.unit) missing.push('Unità');
    if (!item.receipt_status) missing.push('Esito ricevimento');
    if (item.receipt_status === 'non_compliant' && !String(item.receipt_reason || '').trim()) missing.push('Motivo non conformità ricevimento');
    return missing;
  };
  const switchManualItem = idx => {
    if (idx < 0 || idx >= manualItems.length || idx === manualItemIndex) return;
    const current = { ...manual };
    setManualItems(rows => rows.map((row, i) => i === manualItemIndex ? current : row));
    const target = manualItems[idx];
    setManual({ ...target, supplier_id: current.supplier_id, document_ref: current.document_ref, received_date: current.received_date });
    setManualItemIndex(idx);
  };
  const addManualArticle = () => {
    const current = { ...manual };
    const shared = { supplier_id: current.supplier_id, document_ref: current.document_ref, received_date: current.received_date };
    const next = { ...initialManual(), ...shared };
    setManualItems(rows => [...rows.map((row, i) => i === manualItemIndex ? current : row), next]);
    setManual(next);
    setManualItemIndex(manualItems.length);
  };
  const addManualLot = (groupIndices = []) => {
    const sourceIndices = Array.isArray(groupIndices) ? groupIndices : [];
    const current = { ...manual };
    const shared = { supplier_id: current.supplier_id, document_ref: current.document_ref, received_date: current.received_date };
    const sourceIndex = sourceIndices.includes(manualItemIndex) ? manualItemIndex : sourceIndices[0];
    const source = Number.isInteger(sourceIndex)
      ? (sourceIndex === manualItemIndex ? current : manualItems[sourceIndex])
      : current;
    const articleSource = source || current;

    const articleGroupId = articleSource._article_group_id || createReceiptArticleGroupId();
    const next = {
      ...articleSource,
      _article_group_id: articleGroupId,
      ...shared,
      lot_number: '', expiry_date: '', initial_quantity: '', receipt_status: '', receipt_reason: '',
    };
    const currentRows = manualItems.map((row, index) => index === manualItemIndex ? current : row);
    const validGroupIndices = sourceIndices.filter(index => Number.isInteger(index) && currentRows[index]);
    const groupIndexSet = new Set(validGroupIndices.length ? validGroupIndices : [manualItemIndex]);
    const rowsWithStableGroup = currentRows.map((row, index) => groupIndexSet.has(index)
      ? { ...row, _article_group_id: articleGroupId }
      : row);
    setManualItems([...rowsWithStableGroup, next]);
    setManual(next);
    setManualItemIndex(manualItems.length);
  };
  const removeManualItem = idx => {
    const shared = { supplier_id: manual.supplier_id, document_ref: manual.document_ref, received_date: manual.received_date };
    const rows = manualItems.map((row, i) => i === manualItemIndex ? { ...manual } : row).filter((_, i) => i !== idx);
    if (!rows.length) {
      const fresh = { ...initialManual(), ...shared };
      setManualItems([fresh]); setManual(fresh); setManualItemIndex(0); return;
    }
    const nextIndex = Math.min(idx, rows.length - 1);
    const next = { ...rows[nextIndex], ...shared };
    setManualItems(rows.map(row => ({ ...row, ...shared })));
    setManual(next);
    setManualItemIndex(nextIndex);
  };
  const setAllManualCompliant = () => {
    const current = { ...manual, receipt_status: 'compliant', receipt_reason: '' };
    setManual(current);
    setManualItems(rows => rows.map((row, i) => ({ ...(i === manualItemIndex ? current : row), receipt_status: 'compliant', receipt_reason: '' })));
  };
  const changeManualQuantity = delta => setManual(m => {
    const current = Number(m.initial_quantity) || 0;
    const next = Math.max(0, current + delta);
    return { ...m, initial_quantity: String(next) };
  });
  const changeItemQuantity = (idx, delta) => setItems(rows => rows.map((row, i) => {
    if (i !== idx) return row;
    const current = Number(row.quantity) || 0;
    const next = Math.max(0, current + delta);
    return { ...row, quantity: String(next) };
  }));

  const loadReferences = useCallback(async () => {
    try {
      const [ir, ar, sr, alr] = await Promise.all([
        fetch(`${BACKEND_URL}/api/ingredients/`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/articles/`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/suppliers/`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/allergens/`, { credentials: 'include' }),
      ]);
      if (!ir.ok || !ar.ok || !sr.ok || !alr.ok) throw new Error();
      const [ingredientRows, articleRows, supplierRows, allergenRows] = await Promise.all([ir.json(), ar.json(), sr.json(), alr.json()]);
      const validIngredientRows = ingredientRows.filter(i => VALID_INGREDIENT_TYPES.has(i.type));
      const validIds = new Set(validIngredientRows.map(i => i.ingredient_id));
      const coherentArticles = articleRows.filter(a => validIds.has(a.ingredient_id));
      setIngredients(validIngredientRows); setArticles(coherentArticles); setSuppliers(supplierRows.filter(s => s.status !== 'deleted')); setIngredientAllergens(allergenRows);
      return { ingredientRows: validIngredientRows, articleRows: coherentArticles, supplierRows, allergenRows };
    } catch (_) {
      return { ingredientRows: ingredients, articleRows: articles, supplierRows: suppliers, allergenRows: ingredientAllergens };
    }
  }, [ingredients, articles, suppliers, ingredientAllergens]);

  useEffect(() => { loadReferences(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!items.length) { setActiveItemIndex(0); return; }
    setActiveItemIndex(index => Math.min(index, items.length - 1));
  }, [items.length]);

  const releaseCameraStream = useCallback(() => {
    releaseSharedCameraStream(streamRef.current, videoRef.current);
    streamRef.current = null;
  }, []);
  const stopCamera = useCallback(() => { cameraRequestRef.current += 1; cameraStartingRef.current = false; releaseCameraStream(); setCameraReady(false); setTorchAvailable(false); setTorchEnabled(false); setTorchBusy(false); }, [releaseCameraStream]);
  const startCamera = useCallback(async ({ deviceId = '', facingMode = 'environment' } = {}) => {
    const videoElement = videoRef.current;
    if (!videoElement || cameraStartingRef.current) return;
    cameraStartingRef.current = true;
    const requestId = cameraRequestRef.current + 1; cameraRequestRef.current = requestId; releaseCameraStream(); setCameraReady(false); setTorchAvailable(false); setTorchEnabled(false); setCameraError('');
    try {
      const opened = await openCameraStream({ videoElement, deviceId, facingMode, width: 2560, height: 1920 });
      if (requestId !== cameraRequestRef.current) { releaseSharedCameraStream(opened.stream, videoElement); return; }
      streamRef.current = opened.stream; setActiveDeviceId(opened.deviceId); setVideoDevices(opened.devices);
      const videoTrack = opened.stream.getVideoTracks?.()[0];
      await prepareDocumentCamera(videoTrack);
      if (requestId !== cameraRequestRef.current) {
        if (streamRef.current === opened.stream) streamRef.current = null;
        releaseSharedCameraStream(opened.stream, videoElement);
        return;
      }
      await waitForDocumentCameraReady(videoElement, videoTrack);
      if (requestId !== cameraRequestRef.current) {
        if (streamRef.current === opened.stream) streamRef.current = null;
        releaseSharedCameraStream(opened.stream, videoElement);
        return;
      }
      setTorchAvailable(supportsCameraTorch(videoTrack));
      setCameraReady(true);
    } catch (error) {
      if (requestId === cameraRequestRef.current) { releaseCameraStream(); setCameraReady(false); setCameraError(getCameraErrorMessage(error, { fallbackLabel: 'carica un file.' })); }
    } finally {
      if (requestId === cameraRequestRef.current) cameraStartingRef.current = false;
    }
  }, [releaseCameraStream]);
  const handleVideoRef = useCallback(node => {
    videoRef.current = node;
    if (!node || !cameraOpen || capturedPhoto || streamRef.current) return;
    startCamera(activeDeviceId ? { deviceId: activeDeviceId } : { facingMode: 'environment' });
  }, [cameraOpen, capturedPhoto, activeDeviceId, startCamera]);
  useEffect(() => () => { cameraRequestRef.current += 1; releaseCameraStream(); }, [releaseCameraStream]);
  useEffect(() => () => { if (capturedPhoto?.url) URL.revokeObjectURL(capturedPhoto.url); }, [capturedPhoto]);
  useEffect(() => () => { if (typeof preview === 'string' && preview.startsWith('blob:')) URL.revokeObjectURL(preview); }, [preview]);
  useEffect(() => {
    if (currentStep !== 1 || file || cameraOpen || manualOpen) return;
    const frame = window.requestAnimationFrame(() => setCameraOpen(true));
    return () => window.cancelAnimationFrame(frame);
  }, [currentStep, file, cameraOpen, manualOpen]);

  const resetFlow = () => {
    stopCamera(); setCameraOpen(false); setCapturedPhoto(null); setCameraError('');
    setFile(null); setPreview(null); setResult(null); setDocData({ supplier: '', ddt_number: '', ddt_date: '', received_date: localDateToday() });
    setItems([]); setCreatedLots([]); resetManualFlow(); setActiveItemIndex(0); setOcrEditorOpen(false); setManualOpen(false);
  };
  const selectFile = selectedFile => {
    if (!selectedFile) return false;
    const normalizedFile = normalizeKnownFileType(selectedFile); const normalizedType = (normalizedFile.type || '').toLowerCase();
    const isSupportedImage = SUPPORTED_IMAGE_TYPES.has(normalizedType); const accepted = isSupportedImage || normalizedType === 'application/pdf';
    if (!accepted) { toast.error('Formato non supportato. Usa JPG, PNG, WEBP o PDF.'); return false; }
    if (normalizedFile.size > MAX_FILE_SIZE) { toast.error('Il file supera il limite di 10 MB.'); return false; }
    setFile(normalizedFile); setResult(null); setItems([]); setCreatedLots([]); setActiveItemIndex(0);
    if (isSupportedImage) { const reader = new FileReader(); reader.onloadend = () => setPreview(reader.result); reader.readAsDataURL(normalizedFile); }
    else setPreview(URL.createObjectURL(normalizedFile));
    return true;
  };
  const handleFileChange = e => { if (selectFile(e.target.files?.[0])) { stopCamera(); setCameraOpen(false); setCapturedPhoto(null); } e.target.value = ''; };
  const handleReturnToCamera = () => { setFile(null); setPreview(null); setCapturedPhoto(null); setCameraError(''); setCameraOpen(true); };
  const handleSwitchCamera = () => { if (videoDevices.length < 2) return; const currentIndex = videoDevices.findIndex(d => d.deviceId === activeDeviceId); const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % videoDevices.length : 1; startCamera({ deviceId: videoDevices[nextIndex % videoDevices.length].deviceId }); };
  const handleToggleTorch = async () => {
    if (torchBusy) return;
    const track = streamRef.current?.getVideoTracks?.()[0];
    setTorchBusy(true);
    try {
      setTorchEnabled(await setCameraTorch(track, !torchEnabled));
    } catch (error) {
      toast.error(error?.message || 'Impossibile attivare la torcia su questo dispositivo.');
    } finally {
      setTorchBusy(false);
    }
  };
  const handleCapturePhoto = async () => {
    const video = videoRef.current;
    if (!video || !cameraReady || photoCaptureBusy || torchBusy) return toast.error('La fotocamera non è ancora pronta.');
    setPhotoCaptureBusy(true);
    try {
      const track = streamRef.current?.getVideoTracks?.()[0];
      const blob = await captureDocumentPhoto(video, track, { maxDimension: MAX_CAPTURE_DIMENSION, maxFileSize: MAX_FILE_SIZE, quality: 0.9 });
      stopCamera();
      setCapturedPhoto({ blob, url: URL.createObjectURL(blob) });
    } catch (error) {
      toast.error(error?.message || 'Impossibile acquisire la foto.');
    } finally {
      setPhotoCaptureBusy(false);
    }
  };
  const handleRetakePhoto = () => setCapturedPhoto(null);
  const handleRetryCamera = () => startCamera(activeDeviceId ? { deviceId: activeDeviceId } : { facingMode: 'environment' });
  const handleDrop = e => { e.preventDefault(); setDragActive(false); if (selectFile(e.dataTransfer.files?.[0])) { stopCamera(); setCameraOpen(false); setCapturedPhoto(null); } };

  const inferClassification = (item, ingredientRows, articleRows, supplierName) => {
    const code = keyNorm(item.product_code);
    const name = keyNorm(item.product_name || item.commercial_name);
    const supplier = keyNorm(supplierName);
    let supplierMatches = (articleRows || []).filter(a => a.status !== 'deleted' && !a.classification_conflict);
    supplierMatches = supplier ? supplierMatches.filter(a => keyNorm(a.supplier_name) === supplier) : supplierMatches.filter(a => !a.supplier_id);
    let matches = code ? supplierMatches.filter(a => keyNorm(a.product_code) === code) : supplierMatches.filter(a => name && keyNorm(a.commercial_name) === name);
    if (code && matches.length === 0 && name) {
      // If this is the first time OCR sees a code, reuse the name-only Article instead of duplicating it.
      matches = supplierMatches.filter(a => !keyNorm(a.product_code) && keyNorm(a.commercial_name) === name);
    }
    if (matches.length === 1) {
      const article = matches[0];
      const ingredient = (ingredientRows || []).find(row => row.ingredient_id === article.ingredient_id);
      if (!ingredient || !VALID_INGREDIENT_TYPES.has(ingredient.type)) return { article_id: '', article_ingredient_id: '', article_reclassify: false };
      return {
        article_id: article.article_id,
        article_ingredient_id: article.ingredient_id,
        ingredient_id: article.ingredient_id || '',
        article_reclassify: false,
        storage_class: article.storage_class || ingredient?.storage_class || '',
        unit: ingredient?.unit || '',
      };
    }
    return { article_id: '', article_ingredient_id: '', article_reclassify: false };
  };

  const rematchItem = (row, supplierName = docData.supplier) => {
    const inferred = inferClassification(row, selectableIngredients, articles, supplierName);
    const manualFields = new Set(row._manual_fields || []);
    if (inferred.article_id) {
      const preserveIngredient = manualFields.has('ingredient_id');
      const selectedIngredient = preserveIngredient
        ? selectableIngredients.find(item => item.ingredient_id === row.ingredient_id)
        : null;
      return {
        ...row,
        ...inferred,
        ingredient_id: preserveIngredient ? row.ingredient_id : inferred.ingredient_id,
        unit: manualFields.has('unit') ? row.unit : row._ocr_unit || selectedIngredient?.unit || inferred.unit,
        storage_class: manualFields.has('storage_class')
          ? row.storage_class
          : (selectedIngredient?.storage_class || inferred.storage_class),
        article_reclassify: preserveIngredient && row.ingredient_id !== inferred.article_ingredient_id,
      };
    }
    const hadAutomaticClassification = row.article_id && !manualFields.has('ingredient_id') && row.ingredient_id === row.article_ingredient_id;
    const selectedIngredient = manualFields.has('ingredient_id')
      ? selectableIngredients.find(item => item.ingredient_id === row.ingredient_id)
      : null;
    return {
      ...row,
      ...inferred,
      ingredient_id: hadAutomaticClassification ? '' : row.ingredient_id || '',
      unit: manualFields.has('unit') ? row.unit : row._ocr_unit || selectedIngredient?.unit || (hadAutomaticClassification ? '' : row.unit || ''),
      storage_class: manualFields.has('storage_class')
        ? row.storage_class
        : (selectedIngredient?.storage_class || (hadAutomaticClassification ? '' : row.storage_class || '')),
      article_reclassify: false,
    };
  };

  const findManualArticle = row => {
    const supplierId = row.supplier_id === NONE ? null : row.supplier_id;
    const code = keyNorm(row.product_code); const name = keyNorm(row.commercial_name);
    const supplierMatches = articles.filter(a => a.status !== 'deleted' && !a.classification_conflict && (a.supplier_id || null) === supplierId);
    let matches = supplierMatches.filter(a => code ? keyNorm(a.product_code) === code : (name && keyNorm(a.commercial_name) === name));
    if (code && matches.length === 0 && name) matches = supplierMatches.filter(a => !keyNorm(a.product_code) && keyNorm(a.commercial_name) === name);
    return matches.length === 1 ? matches[0] : null;
  };

  const applyManualArticleMatch = next => {
    const match = findManualArticle(next);
    if (match) {
      const ing = selectableIngredients.find(i => i.ingredient_id === match.ingredient_id);
      return { ...next, article_id: match.article_id, article_ingredient_id: match.ingredient_id, article_reclassify: false, ingredient_id: match.ingredient_id, storage_class: match.storage_class || '', unit: next.unit || ing?.unit || '' };
    }
    const hadAutomaticArticleClassification = next.article_id && !next.article_reclassify && next.ingredient_id === next.article_ingredient_id;
    return { ...next, article_id: '', article_ingredient_id: '', article_reclassify: false, ingredient_id: hadAutomaticArticleClassification ? '' : next.ingredient_id };
  };

  const applyManualEditorChange = (current, field, value) => {
    if (field === 'product_name') return applyManualArticleMatch({ ...current, commercial_name: value });
    if (field === 'product_code') return applyManualArticleMatch({ ...current, product_code: value });
    if (field === 'quantity') return { ...current, initial_quantity: value };
    if (field === 'ingredient_id') {
      const ing = selectableIngredients.find(i => i.ingredient_id === value);
      return { ...current, ingredient_id: value, article_reclassify: !!current.article_id && value !== current.article_ingredient_id, unit: current.unit || ing?.unit || '' };
    }
    if (field === 'receipt_status') return { ...current, receipt_status: value, receipt_reason: value === 'compliant' ? '' : current.receipt_reason };
    return { ...current, [field]: value };
  };
  const updateManualEditorField = (field, value) => setManual(current => applyManualEditorChange(current, field, value));
  const updateManualGroupField = (indices, field, value) => {
    const selected = new Set(indices);
    const rows = manualItems.map((row, idx) => idx === manualItemIndex ? { ...manual } : row);
    const nextRows = rows.map((row, idx) => selected.has(idx) ? applyManualEditorChange(row, field, value) : row);
    setManualItems(nextRows);
    setManual({ ...nextRows[manualItemIndex] });
  };
  const updateManualLotField = (idx, field, value) => {
    const source = idx === manualItemIndex ? manual : manualItems[idx];
    if (!source) return;
    const next = applyManualEditorChange(source, field, value);
    setManualItems(rows => rows.map((row, rowIndex) => rowIndex === idx ? next : row));
    if (idx === manualItemIndex) setManual(next);
  };
  const changeManualLotQuantity = (idx, delta) => {
    const updateQuantity = row => ({ ...row, initial_quantity: String(Math.max(0, (Number(row.initial_quantity) || 0) + delta)) });
    setManualItems(rows => rows.map((row, rowIndex) => rowIndex === idx ? updateQuantity(rowIndex === manualItemIndex ? manual : row) : row));
    if (idx === manualItemIndex) setManual(row => updateQuantity(row));
  };

  const handleUpload = async (sourceFile = file) => {
    if (!sourceFile) return toast.error('Seleziona un file'); setUploading(true);
    try {
      const uploadData = new FormData(); uploadData.append('file', sourceFile);
      const response = await fetch(`${BACKEND_URL}/api/ocr/upload`, { method: 'POST', body: uploadData, credentials: 'include' });
      if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err.detail || 'Upload fallito'); }
      const data = await response.json(); const extracted = data.extracted_data || {};
      const extractedItems = Array.isArray(extracted.items) ? extracted.items : [];
      if (extracted.extraction_status === 'not_usable' || !extractedItems.some(i => i && typeof i === 'object' && (i.product_name || i.product_code || i.lot_number))) {
        toast.error('Non sono state rilevate righe leggibili. Acquisisci di nuovo il documento oppure usa l’inserimento manuale.');
        return;
      }
      const refs = await loadReferences(); const nextDoc = { supplier: extracted.supplier || '', ddt_number: extracted.ddt_number || '', ddt_date: extracted.ddt_date || '', received_date: localDateToday() };
      setResult(data); setDocData(nextDoc);
      setItems(extractedItems.map(i => {
        const base = { product_code: i.product_code || '', product_name: i.product_name || '', lot_number: i.lot_number || '', expiry_date: i.expiry_date || '', quantity: i.quantity != null ? String(i.quantity) : '', unit: i.unit || '', _ocr_unit: i.unit || '', ingredient_id: '', article_id: '', article_ingredient_id: '', article_reclassify: false, storage_class: '', receipt_status: '', receipt_reason: '', internal_lot_confirmed: false, _manual_fields: [] };
        const inferred = inferClassification(base, refs.ingredientRows, refs.articleRows, nextDoc.supplier);
        return { ...base, ...inferred, warnings: Array.isArray(i.warnings) ? i.warnings : [] };
      }));
      setActiveItemIndex(0);
      setOcrEditorOpen(true);
      toast.success('Documento analizzato. Verifica e classifica le righe estratte.');
    } catch (error) { toast.error(error.message); }
    finally { setUploading(false); }
  };


  const handleAnalyze = () => {
    if (file) return handleUpload(file);
    if (!capturedPhoto?.blob) return toast.error('Scatta una foto o carica un documento');
    const capturedFile = new File([capturedPhoto.blob], `foto_documento_${Date.now()}.jpg`, { type: 'image/jpeg' });
    if (capturedFile.size > MAX_FILE_SIZE) return toast.error('La foto supera il limite di 10 MB. Rifai la foto oppure carica un documento.');
    return handleUpload(capturedFile);
  };

  const applyOcrItemChange = (row, field, value) => {
    const manualFields = new Set(row._manual_fields || []);
    manualFields.add(field);
    if (field === 'ingredient_id') {
      const ingredient = selectableIngredients.find(item => item.ingredient_id === value);
      return {
        ...row,
        ingredient_id: value,
        unit: manualFields.has('unit') ? row.unit : row._ocr_unit || ingredient?.unit || '',
        storage_class: manualFields.has('storage_class') ? row.storage_class : ingredient?.storage_class || '',
        _manual_fields: [...manualFields],
        article_reclassify: !!row.article_id && value !== row.article_ingredient_id,
      };
    }
    const next = { ...row, [field]: value, _manual_fields: [...manualFields] };
    if (field === 'lot_number' && String(value || '').trim()) next.internal_lot_confirmed = false;
    return (field === 'product_name' || field === 'product_code') ? rematchItem(next) : next;
  };
  const updateItem = (idx, field, value) => setItems(rows => rows.map((row, i) => i === idx ? applyOcrItemChange(row, field, value) : row));
  const updateItemGroup = (indices, field, value) => {
    const selected = new Set(indices);
    setItems(rows => rows.map((row, i) => selected.has(i) ? applyOcrItemChange(row, field, value) : row));
  };
  const updateDocumentSupplier = value => {
    setDocData(v => ({ ...v, supplier: value }));
    setItems(rows => rows.map(row => rematchItem(row, value)));
  };
  const addItem = () => {
    const nextIndex = items.length;
    setItems(rows => [...rows, emptyItem()]);
    setActiveItemIndex(nextIndex);
  };
  const addOcrLot = (groupIndices = []) => {
    const sourceIndices = Array.isArray(groupIndices) ? groupIndices : [];
    const preferredSourceIndex = sourceIndices.includes(visibleItemIndex) ? visibleItemIndex : (sourceIndices[0] ?? visibleItemIndex);
    const sourceIndex = Number.isInteger(preferredSourceIndex) && items[preferredSourceIndex]
      ? preferredSourceIndex
      : visibleItemIndex;
    const source = items[sourceIndex];
    if (!source) return;
    const nextIndex = items.length;
    const articleGroupId = source._article_group_id || createReceiptArticleGroupId();
    const next = {
      ...source,
      _article_group_id: articleGroupId,
      lot_number: '', expiry_date: '', quantity: '', receipt_status: '', receipt_reason: '',
      internal_lot_confirmed: false, warnings: [],
    };
    const validGroupIndices = sourceIndices.filter(index => Number.isInteger(index) && items[index]);
    const groupIndexSet = new Set(validGroupIndices.length ? validGroupIndices : [sourceIndex]);
    setItems(rows => [...rows.map((row, index) => groupIndexSet.has(index)
      ? { ...row, _article_group_id: articleGroupId }
      : row), next]);
    setActiveItemIndex(nextIndex);
  };
  const removeItem = idx => {
    const nextLength = Math.max(0, items.length - 1);
    setItems(rows => rows.filter((_, i) => i !== idx));
    setActiveItemIndex(current => {
      if (!nextLength) return 0;
      if (idx < current) return Math.max(0, current - 1);
      return Math.min(current, nextLength - 1);
    });
  };
  const openNewIngredient = (idx, target = 'ocr', groupIndices = []) => { const item = target === 'manual' ? manual : items[idx]; setNewIngredient({ idx, target, groupIndices, name: '', unit: item?.unit || 'kg', type: '', allergens: [], allergens_verified: false }); };
  const saveNewIngredient = async () => {
    if (!newIngredient?.name.trim() || !VALID_INGREDIENT_TYPES.has(newIngredient?.type)) return toast.error('Nome e tipologia sono obbligatori');
    if (!newIngredient.allergens_verified) return toast.error('Verifica gli allergeni oppure seleziona “Nessun allergene dichiarato”');
    try {
      const r = await fetch(`${BACKEND_URL}/api/ingredients/`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ name: newIngredient.name.trim(), unit: newIngredient.unit || 'kg', allergens: newIngredient.allergens, allergens_verified: true, notes: '', type: newIngredient.type }) });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Errore creazione ingrediente'); }
      const created = await r.json(); setIngredients(v => [...v, created]);
      if (newIngredient.target === 'manual') {
        if (newIngredient.groupIndices?.length) {
          updateManualGroupField(newIngredient.groupIndices, 'ingredient_id', created.ingredient_id);
          setManualItems(rows => rows.map((row, idx) => newIngredient.groupIndices.includes(idx) ? { ...row, unit: row.unit || created.unit || '' } : row));
          setManual(row => ({ ...row, ingredient_id: created.ingredient_id, unit: row.unit || created.unit || '' }));
        } else setManual(m => ({ ...m, ingredient_id: created.ingredient_id, unit: m.unit || created.unit || '' }));
      } else if (newIngredient.groupIndices?.length) {
        updateItemGroup(newIngredient.groupIndices, 'ingredient_id', created.ingredient_id);
        setItems(rows => rows.map((row, idx) => newIngredient.groupIndices.includes(idx) ? { ...row, unit: row.unit || row._ocr_unit || created.unit || '' } : row));
      } else updateItem(newIngredient.idx, 'ingredient_id', created.ingredient_id);
      setNewIngredient(null); toast.success('Ingrediente creato e associato');
    } catch (e) { toast.error(e.message); }
  };

  const openAllergenEdit = ingredient => {
    if (!ingredient?.ingredient_id) return;
    setAllergenEdit({
      ingredient_id: ingredient.ingredient_id,
      name: ingredient.name || 'Ingrediente',
      allergens: [...(ingredient.allergens || [])],
      allergens_verified: ingredient.allergens_verified === true,
    });
  };
  const saveAllergenEdit = async () => {
    if (!allergenEdit?.ingredient_id) return;
    if (allergenEdit.allergens_verified !== true) return toast.error('Verifica gli allergeni oppure seleziona “Nessun allergene dichiarato”');
    setAllergenSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/ingredients/${allergenEdit.ingredient_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ allergens: allergenEdit.allergens || [], allergens_verified: true }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Impossibile aggiornare gli allergeni'); }
      const updated = await r.json();
      setIngredients(rows => rows.map(row => row.ingredient_id === updated.ingredient_id ? { ...row, ...updated } : row));
      setAllergenEdit(null);
      toast.success('Allergeni aggiornati');
    } catch (e) { toast.error(e.message || 'Impossibile aggiornare gli allergeni'); }
    finally { setAllergenSaving(false); }
  };

  const handleCreateLots = async () => {
    if (!result || !items.length) return;
    if (!String(docData.supplier || '').trim()) return toast.error('Indica il fornitore prima di creare i lotti');
    const missingByRow = items.map((item, idx) => ({ idx, missing: getItemMissingFields(item) })).filter(row => row.missing.length);
    if (missingByRow.length) {
      setActiveItemIndex(missingByRow[0].idx);
      return toast.error(`Completa l’articolo ${missingByRow[0].idx + 1}: ${missingByRow[0].missing.join(', ')}`);
    }
    setCreatingLots(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/ocr/create-lots/${result.document_id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ supplier: docData.supplier, ddt_number: docData.ddt_number, ddt_date: docData.ddt_date, received_date: docData.received_date || localDateToday(), items: items.map(({ _article_group_id, _manual_fields, _ocr_unit, warnings, ...i }) => ({ ...i, quantity: Number(i.quantity) })) })
      });
      if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err.detail || 'Creazione lotti fallita'); }
      const data = await response.json(); setCreatedLots(data.lots || []); setOcrEditorOpen(false); await loadReferences(); toast.success(data.already_created ? 'I lotti di questo documento erano già stati registrati.' : `${data.created} lotti creati`);
    } catch (e) { toast.error(e.message); }
    finally { setCreatingLots(false); }
  };

  const saveManualLot = async () => {
    if (!manual.supplier_id || manual.supplier_id === NONE) return toast.error('Seleziona il fornitore');
    const rows = manualItems.map((row, idx) => idx === manualItemIndex ? { ...manual } : { ...row, supplier_id: manual.supplier_id, document_ref: manual.document_ref, received_date: manual.received_date });
    const invalidIndex = rows.findIndex(row => manualItemMissing(row).length > 0);
    if (invalidIndex >= 0) {
      const missing = manualItemMissing(rows[invalidIndex]);
      const articleNumber = groupReceiptItemsByArticle(rows.map(manualToEditorItem), manual.supplier_id)
        .findIndex(group => group.indexes.includes(invalidIndex)) + 1;
      setManualItems(rows);
      setManualItemIndex(invalidIndex);
      setManual({ ...rows[invalidIndex] });
      return toast.error(`Completa articolo ${articleNumber}: ${missing.join(', ')}`);
    }
    setManualSaving(true);
    const created = [];
    try {
      for (const item of rows) {
        const r = await fetch(`${BACKEND_URL}/api/lots/`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({
          ingredient_id: item.ingredient_id, storage_class: item.storage_class, article_id: item.article_id || null, article_reclassify: !!item.article_reclassify, supplier_id: manual.supplier_id, lot_number: item.lot_number.trim(),
          received_date: manual.received_date || new Date().toISOString().slice(0, 10), expiry_date: item.expiry_date, initial_quantity: Number(item.initial_quantity), unit: item.unit,
          document_ref: manual.document_ref.trim() || null, origin: 'manual', commercial_name: item.commercial_name.trim(), product_code: item.product_code.trim(), receipt_status: item.receipt_status, receipt_reason: item.receipt_status === 'non_compliant' ? item.receipt_reason.trim() : null
        }) });
        if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || `Errore registrazione articolo ${created.length + 1}`); }
        created.push(await r.json());
      }
      setCreatedLots(created); resetManualFlow(); setManualOpen(false); await loadReferences(); toast.success(created.length === 1 ? 'Lotto registrato' : `${created.length} lotti registrati`);
    } catch (e) {
      if (created.length > 0) {
        const remaining = rows.slice(created.length);
        if (remaining.length > 0) {
          setManualItems(remaining);
          setManualItemIndex(0);
          setManual({ ...remaining[0] });
        }
        await loadReferences();
        toast.error(`${created.length} ${created.length === 1 ? 'articolo registrato' : 'articoli registrati'}; ${e.message}. Restano solo gli articoli non registrati.`);
      } else {
        toast.error(e.message);
      }
    }
    finally { setManualSaving(false); }
  };

  const openEditLot = lot => setEditLot({ ...lot, expiry_date: dateInput(lot.expiry_date), received_date: dateInput(lot.received_date), supplier_id: lot.supplier_id || NONE, receipt_status: lot.receipt_status || '', receipt_reason: lot.receipt_reason || '' });
  const saveEditLot = async () => {
    if (!editLot?.ingredient_id || !editLot.lot_number?.trim() || !editLot.expiry_date) return toast.error('Ingrediente, lotto e scadenza sono obbligatori');
    if (editLot.receipt_status === 'non_compliant' && !String(editLot.receipt_reason || '').trim()) return toast.error('Indica il motivo del ricevimento non conforme');
    setEditSaving(true);
    try {
      const payload = {
        ingredient_id: editLot.ingredient_id, supplier_id: editLot.supplier_id === NONE ? null : editLot.supplier_id, lot_number: editLot.lot_number,
        expiry_date: editLot.expiry_date, commercial_name: editLot.commercial_name || '', product_code: editLot.product_code || '', document_ref: editLot.document_ref || ''
      };
      if (!editLot.receipt_status) return toast.error('Seleziona l’esito ricevimento');
      payload.receipt_status = editLot.receipt_status;
      payload.receipt_reason = editLot.receipt_status === 'non_compliant' ? String(editLot.receipt_reason || '').trim() : null;
      const r = await fetch(`${BACKEND_URL}/api/lots/${editLot.lot_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(payload) });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Errore modifica lotto'); }
      const updated = await r.json(); const ingredient = updated.ingredient || selectableIngredients.find(i => i.ingredient_id === updated.ingredient_id); const supplier = updated.supplier || suppliers.find(s => s.supplier_id === updated.supplier_id);
      setCreatedLots(rows => rows.map(l => l.lot_id === updated.lot_id ? { ...l, ...updated, ingredient_name: ingredient?.name || l.ingredient_name, supplier_name: supplier?.name || '-' } : l)); setEditLot(null); await loadReferences(); toast.success('Lotto aggiornato');
    } catch (e) { toast.error(e.message); }
    finally { setEditSaving(false); }
  };


  const manualRowsForEditor = manualItems.map((row, idx) => idx === manualItemIndex ? { ...manual } : row);
  const manualEditorItems = manualRowsForEditor.map(manualToEditorItem);
  const manualEditorStates = manualRowsForEditor.map(row => { const missing = manualItemMissing(row); return { missing, complete: missing.length === 0 }; });
  const displayStep = manualOpen ? 2 : currentStep;
  const setAllOcrCompliant = () => setItems(rows => rows.map(row => ({ ...row, receipt_status: 'compliant', receipt_reason: '' })));
  const updateOcrEditorField = (field, value) => {
    updateItem(visibleItemIndex, field, value);
    if (field === 'receipt_status' && value === 'compliant') updateItem(visibleItemIndex, 'receipt_reason', '');
  };
  const updateOcrLotField = (idx, field, value) => setItems(rows => rows.map((row, rowIndex) => {
    if (rowIndex !== idx) return row;
    const next = applyOcrItemChange(row, field, value);
    if (field === 'receipt_status' && value === 'compliant') next.receipt_reason = '';
    return next;
  }));
  const exitOcrEditor = () => {
    setOcrEditorOpen(false);
    setResult(null);
    setItems([]);
    setActiveItemIndex(0);
  };

  return <div data-testid="ocr-page" data-ocr-step={currentStep} className={`app-page ${currentStep === 1 ? 'h-full min-h-0 !py-2 sm:!py-3 md:!py-4' : currentStep === 2 ? 'h-full min-h-0 !py-2 sm:!py-3 md:!py-4' : ''}`}><div className={`app-page-inner ${currentStep === 1 || currentStep === 2 ? 'h-full min-h-0 flex flex-col' : ''}`}>
    <div className={`${currentStep === 2 ? 'hidden sm:block ' : ''}${currentStep === 1 || currentStep === 2 ? 'shrink-0' : ''}`}><PageHeader icon="goodsReceipt" title="Carica merce" /></div>

    <div className={`${currentStep === 1 || currentStep === 2 ? 'shrink-0 mt-1.5' : ''} p-0`}>
      <div className="mx-auto flex max-w-3xl items-center gap-2 px-2 py-1 sm:gap-3 sm:px-4 sm:py-1.5">
        {steps.map((step, index) => { const done = displayStep > step.n; const active = displayStep === step.n; return <React.Fragment key={step.n}>
          <div className="flex shrink-0 items-center gap-2">
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold sm:text-sm ${active ? 'bg-primary text-white' : done ? 'bg-green-600 text-white' : 'bg-slate-200 text-slate-600'}`}>{done ? <Check className="h-4 w-4"/> : step.n}</div>
            <span className={`hidden text-sm font-semibold sm:inline ${active ? 'text-primary' : done ? 'text-green-800' : 'text-slate-500'}`}>{step.label}</span>
          </div>
          {index < steps.length - 1 && <div className={`h-px min-w-5 flex-1 ${displayStep > step.n ? 'bg-green-500' : 'bg-slate-200'}`}/>} 
        </React.Fragment>; })}
      </div>
    </div>

    {currentStep === 1 && <div className="!mt-2 mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col">
      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf,.jpg,.jpeg,.png,.webp,.pdf" onChange={handleFileChange} className="hidden" />
      <div className="shrink-0 pb-2 pt-1"><h2 className="font-heading text-base font-medium sm:text-lg">Scatta la foto al tuo documento: DDT o etichetta</h2></div>
      <div className="flex min-h-0 flex-1 flex-col gap-2 sm:gap-2.5">
        <div onDragOver={e => { e.preventDefault(); setDragActive(true); }} onDragLeave={() => setDragActive(false)} onDrop={handleDrop} className={`relative min-h-0 flex-1 overflow-hidden rounded-xl border bg-black transition-colors ${dragActive ? 'border-primary ring-2 ring-primary/20' : 'border-border'}`}>
          <div className="relative h-full min-h-0 w-full overflow-hidden bg-black">
            {file && preview ? (file.type === 'application/pdf' ? <iframe title="Anteprima documento" src={preview} className="h-full w-full bg-white"/> : <img src={preview} alt="Documento acquisito" className="h-full w-full object-contain"/>) : capturedPhoto ? <img src={capturedPhoto.url} alt="Foto acquisita" className="h-full w-full object-contain"/> : <>
              <video ref={handleVideoRef} autoPlay muted playsInline className="h-full w-full object-contain"/>
              {!cameraError && !cameraReady && <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/40 text-white"><Loader2 className="h-7 w-7 animate-spin"/><span className="text-xs sm:text-sm">Avvio fotocamera...</span></div>}
              {cameraError && <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5 bg-black/75 p-4 text-center text-white"><CameraOff className="h-9 w-9"/><p className="max-w-md text-xs sm:text-sm">{cameraError}</p><Button type="button" size="sm" variant="outline" className="bg-white text-slate-900 hover:bg-slate-100" onClick={handleRetryCamera}><RefreshCw className="h-4 w-4"/>Riprova fotocamera</Button></div>}
              {!cameraError && torchAvailable && <button type="button" onClick={handleToggleTorch} disabled={!cameraReady || torchBusy || photoCaptureBusy} title={torchEnabled ? 'Disattiva torcia' : 'Attiva torcia'} aria-label={torchEnabled ? 'Disattiva torcia' : 'Attiva torcia'} aria-pressed={torchEnabled} className={`absolute left-2.5 top-2.5 inline-flex h-10 items-center justify-center gap-1.5 rounded-full border border-white/50 px-3 text-xs font-semibold shadow-md backdrop-blur-sm transition disabled:opacity-40 sm:left-3 sm:top-3 ${torchEnabled ? 'bg-amber-400 text-slate-950 hover:bg-amber-300' : 'bg-black/45 text-white hover:bg-black/65'}`}><Flashlight className="h-4 w-4"/><span>Torcia</span></button>}
              {!cameraError && videoDevices.length > 1 && <button type="button" onClick={handleSwitchCamera} disabled={!cameraReady || torchBusy || photoCaptureBusy} title="Cambia fotocamera" aria-label="Cambia fotocamera" className="absolute right-2.5 top-2.5 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/50 bg-black/45 text-white shadow-md backdrop-blur-sm transition hover:bg-black/65 disabled:opacity-40 sm:right-3 sm:top-3"><RefreshCw className="h-5 w-5"/></button>}
              {!cameraError && <div className="absolute bottom-2.5 left-1/2 flex -translate-x-1/2 flex-col items-center gap-1 sm:bottom-3">
                <button type="button" onClick={handleCapturePhoto} disabled={!cameraReady || torchBusy || photoCaptureBusy} aria-label={photoCaptureBusy ? 'Acquisizione foto' : 'Scatta foto'} className="inline-flex h-14 w-14 items-center justify-center rounded-full border-4 border-white/90 bg-amber-500 text-slate-950 shadow-xl transition hover:bg-amber-400 disabled:opacity-45 sm:h-16 sm:w-16">{photoCaptureBusy ? <Loader2 className="h-6 w-6 animate-spin sm:h-7 sm:w-7"/> : <Camera className="h-6 w-6 sm:h-7 sm:w-7"/>}</button>
                <span className="rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-semibold text-white shadow-sm sm:text-xs">{photoCaptureBusy ? 'Acquisizione…' : 'Scatta'}</span>
              </div>}
            </>}

            {file && preview && <button type="button" onClick={handleReturnToCamera} disabled={uploading} className="absolute right-2.5 top-2.5 z-10 inline-flex min-h-10 items-center gap-1.5 rounded-full border border-white/50 bg-black/55 px-3 text-xs font-semibold text-white shadow-md backdrop-blur-sm transition hover:bg-black/70 disabled:opacity-45 sm:right-3 sm:top-3 sm:text-sm"><Camera className="h-4 w-4"/>Scatta nuova foto</button>}
            {capturedPhoto && !file && <button type="button" onClick={handleRetakePhoto} className="absolute right-2.5 top-2.5 inline-flex min-h-10 items-center gap-1.5 rounded-full border border-white/50 bg-black/55 px-3 text-xs font-semibold text-white shadow-md backdrop-blur-sm transition hover:bg-black/70 sm:right-3 sm:top-3 sm:text-sm"><RefreshCw className="h-4 w-4"/>Rifai foto</button>}
          </div>
        </div>

        <div className="grid shrink-0 grid-cols-2 gap-2">
          <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="h-auto min-h-[52px] justify-start whitespace-normal px-2.5 py-2 sm:min-h-[56px] sm:px-3">
            <Upload className="h-5 w-5 shrink-0"/>
            <span className="min-w-0 text-left leading-tight"><span className="block text-xs font-semibold sm:text-sm">Carica documento</span><span className="mt-0.5 block text-[10px] font-normal text-muted-foreground sm:text-xs">JPG, PNG, WEBP o PDF · massimo 10 MB</span></span>
          </Button>
          <Button type="button" variant="outline" onClick={() => { stopCamera(); setCameraOpen(false); setManualOpen(true); }} disabled={uploading} className="h-auto min-h-[52px] justify-start whitespace-normal px-2.5 py-2 sm:min-h-[56px] sm:px-3">
            <Plus className="h-5 w-5 shrink-0"/>
            <span className="min-w-0 text-left leading-tight"><span className="block text-xs font-semibold sm:text-sm">Inserisci manualmente</span><span className="mt-0.5 block text-[10px] font-normal text-muted-foreground sm:text-xs">Compila lotto e dati senza AI</span></span>
          </Button>
        </div>

        <Button className="h-11 w-full shrink-0 btn-primary sm:h-12 md:h-12 xl:h-12" onClick={handleAnalyze} disabled={(!file && !capturedPhoto) || uploading}>{uploading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin"/>Analisi AI...</> : <><ScanLine className="w-5 h-5 mr-2"/>ANALIZZA CON L'AI</>}</Button>
      </div>
    </div>}

    {currentStep === 2 && <Card className="card-base !mt-2 mx-auto flex min-h-0 w-full max-w-4xl flex-1 items-center justify-center">
      <CardContent className="flex w-full max-w-md flex-col items-center gap-3 p-5 text-center sm:p-6">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary"><CheckCircle2 className="h-6 w-6"/></div>
        <div><p className="font-heading text-lg font-semibold">Documento analizzato</p><p className="mt-1 text-sm text-muted-foreground">La verifica del carico si apre nel pannello ancorato in basso.</p></div>
        <Button type="button" className="h-11 min-w-44" onClick={() => setOcrEditorOpen(true)}>Continua verifica</Button>
      </CardContent>
    </Card>}

    {currentStep === 3 && <div className="space-y-4">
      <div className="surface p-4 sm:p-5 flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-green-100 text-green-700 flex items-center justify-center"><CheckCircle2 className="w-6 h-6"/></div><div><h2 className="font-heading text-lg font-medium">Merce registrata</h2><p className="text-sm text-muted-foreground">{createdLots.length} {createdLots.length === 1 ? 'lotto creato' : 'lotti creati'}</p></div></div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {createdLots.map(lot => <Card key={lot.lot_id} className="card-base h-full"><CardContent className="p-4 flex flex-col h-full gap-3">
          <div className="flex items-start gap-3"><ProductThumbnail item={lot} size="tablet"/><div className="min-w-0"><p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Articolo</p><p className="font-semibold text-base leading-tight">{lot.commercial_name || lot.ingredient_name || 'Prodotto'}</p>{lot.product_code && <p className="text-xs text-muted-foreground mt-1">Codice {lot.product_code}</p>}</div></div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm flex-1"><span className="text-muted-foreground">Ingrediente</span><span className="font-medium text-right">{lot.ingredient_name || selectableIngredients.find(i => i.ingredient_id === lot.ingredient_id)?.name || '—'}</span><span className="text-muted-foreground">Lotto</span><span className="text-right"><EntityBadge entity="lot" label={lot.lot_number} compact/></span><span className="text-muted-foreground">Scadenza</span><span className="text-right">{dateInput(lot.expiry_date).split('-').reverse().join('/') || '—'}</span><span className="text-muted-foreground">Quantità</span><span className="text-right">{lot.current_quantity ?? lot.initial_quantity} {lot.unit}</span><span className="text-muted-foreground">Fornitore</span><span className="text-right">{lot.supplier_name || '—'}</span><span className="text-muted-foreground">Esito ricevimento</span><span className="text-right"><StatusBadge status={lot.receipt_status}/></span></div>
          <div className="grid grid-cols-2 gap-2 pt-1"><Button variant="outline" onClick={() => openEditLot(lot)}><Edit3 className="w-4 h-4 mr-1"/>Modifica</Button><Button onClick={() => setPrintLot(lot)}><Printer className="w-4 h-4 mr-1"/>Etichetta</Button></div>
        </CardContent></Card>)}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl mx-auto"><Button variant="outline" onClick={resetFlow}><RefreshCw className="w-4 h-4 mr-2"/>Carica altra merce</Button><Button onClick={() => navigate('/magazzino')}><Warehouse className="w-4 h-4 mr-2"/>Vai al magazzino</Button></div>
    </div>}


    <Dialog open={!!allergenEdit} onOpenChange={open => !open && setAllergenEdit(null)}><ViewportDialogContent active={!!allergenEdit} className="w-[calc(100%_-_1rem)] max-h-[92dvh] overflow-y-auto sm:max-w-lg"><DialogHeader><DialogTitle>Allergeni · {allergenEdit?.name || 'Ingrediente'}</DialogTitle><DialogDescription>Controlla gli allergeni dichiarati e conferma la selezione.</DialogDescription></DialogHeader>{allergenEdit && <div className="space-y-3"><AllergenSelector compact allergens={ingredientAllergens} selected={allergenEdit.allergens || []} verified={allergenEdit.allergens_verified === true} onChange={(selected, verified) => setAllergenEdit(value => ({ ...value, allergens: selected, allergens_verified: verified }))}/><Button className="w-full" onClick={saveAllergenEdit} disabled={allergenSaving || allergenEdit.allergens_verified !== true}>{allergenSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin"/>Salvataggio...</> : 'Salva allergeni'}</Button></div>}</ViewportDialogContent></Dialog>

    <ReceiptEditorSheet
      open={ocrEditorOpen && currentStep === 2}
      onOpenChange={setOcrEditorOpen}
      supplierMode="text"
      supplierValue={docData.supplier}
      onSupplierChange={updateDocumentSupplier}
      documentRef={docData.ddt_number}
      onDocumentRefChange={value => setDocData(v => ({ ...v, ddt_number: value }))}
      documentDate={dateInput(docData.ddt_date)}
      onDocumentDateChange={value => setDocData(v => ({ ...v, ddt_date: value }))}
      receivedDate={dateInput(docData.received_date)}
      onReceivedDateChange={value => setDocData(v => ({ ...v, received_date: value }))}
      items={items}
      itemStates={itemStates}
      activeIndex={visibleItemIndex}
      onActiveIndexChange={setActiveItemIndex}
      onAddItem={addItem}
      onRemoveItem={removeItem}
      onItemFieldChange={updateOcrEditorField}
      onGroupFieldChange={updateItemGroup}
      onLotFieldChange={updateOcrLotField}
      onLotQuantityDelta={changeItemQuantity}
      onAddLot={addOcrLot}
      onQuantityDelta={delta => changeItemQuantity(visibleItemIndex, delta)}
      onMarkAllCompliant={setAllOcrCompliant}
      ingredients={selectableIngredients}
      onCreateIngredient={groupIndices => openNewIngredient(visibleItemIndex, 'ocr', groupIndices)}
      onEditAllergens={openAllergenEdit}
      lotRequired={false}
      allowInternalLot
      busy={creatingLots}
      onSubmit={handleCreateLots}
      onExit={exitOcrEditor}
      errorNotice={result?.extracted_data?.error || ''}
      extractionWarnings={(result?.extracted_data?.warnings || []).filter(warning => !(String(warning || '').toLowerCase().includes('data ddt') && docData.ddt_date))}
    />

    <ReceiptEditorSheet
      open={manualOpen}
      onOpenChange={open => { setManualOpen(open); if (!open) resetManualFlow(); }}
      supplierMode="select"
      supplierValue={manual.supplier_id === NONE ? '' : manual.supplier_id}
      onSupplierChange={value => setManual(current => applyManualArticleMatch({ ...current, supplier_id: value }))}
      suppliers={suppliers}
      documentRef={manual.document_ref}
      onDocumentRefChange={value => setManual(current => ({ ...current, document_ref: value }))}
      documentDate={manual.received_date}
      onDocumentDateChange={value => setManual(current => ({ ...current, received_date: value }))}
      items={manualEditorItems}
      itemStates={manualEditorStates}
      activeIndex={manualItemIndex}
      onActiveIndexChange={switchManualItem}
      onAddItem={addManualArticle}
      onRemoveItem={removeManualItem}
      onItemFieldChange={updateManualEditorField}
      onGroupFieldChange={updateManualGroupField}
      onLotFieldChange={updateManualLotField}
      onLotQuantityDelta={changeManualLotQuantity}
      onAddLot={addManualLot}
      onQuantityDelta={changeManualQuantity}
      onMarkAllCompliant={setAllManualCompliant}
      ingredients={selectableIngredients}
      onCreateIngredient={groupIndices => openNewIngredient(manualItemIndex, 'manual', groupIndices)}
      onEditAllergens={openAllergenEdit}
      lotRequired
      busy={manualSaving}
      onSubmit={saveManualLot}
      onExit={() => { resetManualFlow(); setManualOpen(false); }}
    />

    <Dialog open={!!newIngredient} onOpenChange={o => !o && setNewIngredient(null)}><ViewportDialogContent active={!!newIngredient} className="w-[calc(100%_-_1rem)] sm:max-w-md"><DialogHeader><DialogTitle>Nuovo ingrediente</DialogTitle><DialogDescription>Crea un ingrediente da associare agli articoli.</DialogDescription></DialogHeader>{newIngredient && <div className="space-y-3"><div><Label>Nome *</Label><Input value={newIngredient.name} onChange={e => setNewIngredient(v => ({ ...v, name: e.target.value }))} placeholder="Es. Farina 00"/></div><div><Label>Tipologia *</Label><Select value={newIngredient.type || undefined} onValueChange={v => setNewIngredient(x => ({ ...x, type: v }))}><SelectTrigger><SelectValue placeholder="Seleziona tipologia"/></SelectTrigger><SelectContent><SelectItem value="raw">Materia prima</SelectItem><SelectItem value="semifinished">Semilavorato</SelectItem><SelectItem value="final_product">Prodotto finito</SelectItem></SelectContent></Select></div><div><Label>Unità</Label><UnitSelect value={newIngredient.unit} onValueChange={v => setNewIngredient(x => ({ ...x, unit: v }))}/></div><AllergenSelector compact allergens={ingredientAllergens} selected={newIngredient.allergens || []} verified={newIngredient.allergens_verified === true} onChange={(selected, verified) => setNewIngredient(x => ({ ...x, allergens: selected, allergens_verified: verified }))}/><Button className="w-full" onClick={saveNewIngredient}>Crea e associa</Button></div>}</ViewportDialogContent></Dialog>

    <Dialog open={!!editLot} onOpenChange={o => !o && setEditLot(null)}><ViewportDialogContent active={!!editLot} className="w-[calc(100%_-_1rem)] sm:max-w-xl max-h-[94dvh] overflow-y-auto"><DialogHeader><DialogTitle>Modifica lotto</DialogTitle><DialogDescription>La quantità non viene modificata qui: eventuali rettifiche restano tracciate nei movimenti di magazzino.</DialogDescription></DialogHeader>{editLot && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="sm:col-span-2"><Label>Nome articolo (snapshot lotto)</Label><Input value={editLot.commercial_name || ''} onChange={e => setEditLot(v => ({ ...v, commercial_name: e.target.value }))}/></div>
      <div><Label>Ingrediente *</Label><IngredientSearchSelect value={editLot.ingredient_id} onValueChange={v => setEditLot(l => ({ ...l, ingredient_id: v }))} ingredients={selectableIngredients}/></div>
      <div><Label>Codice articolo</Label><Input value={editLot.product_code || ''} onChange={e => setEditLot(v => ({ ...v, product_code: e.target.value }))}/></div>
      <div><Label>Fornitore</Label><Select value={editLot.supplier_id || NONE} onValueChange={v => setEditLot(l => ({ ...l, supplier_id: v }))}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value={NONE}>Nessun fornitore</SelectItem>{suppliers.map(s => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
      <div><Label>Lotto *</Label><Input value={editLot.lot_number || ''} onChange={e => setEditLot(v => ({ ...v, lot_number: e.target.value }))}/></div>
      <div><Label>Scadenza *</Label><Input type="date" value={editLot.expiry_date || ''} onChange={e => setEditLot(v => ({ ...v, expiry_date: e.target.value }))}/></div>
      <div><Label>DDT / riferimento</Label><Input value={editLot.document_ref || ''} onChange={e => setEditLot(v => ({ ...v, document_ref: e.target.value }))}/></div>
      <div className="sm:col-span-2"><Label>Esito ricevimento</Label><ReceiptStatusSelector value={editLot.receipt_status || ''} onChange={v => setEditLot(l => ({ ...l, receipt_status: v, receipt_reason: v !== 'non_compliant' ? '' : l.receipt_reason }))}/></div>
      {editLot.receipt_status === 'non_compliant' && <div className="sm:col-span-2"><Label>Motivo non conformità *</Label><Input value={editLot.receipt_reason || ''} onChange={e => setEditLot(v => ({ ...v, receipt_reason: e.target.value }))}/></div>}
      <Button className="sm:col-span-2" onClick={saveEditLot} disabled={editSaving}>{editSaving ? 'Salvataggio...' : 'Salva modifiche'}</Button>
    </div>}</ViewportDialogContent></Dialog>

    <LabelPrintDialog
      open={!!printLot}
      onOpenChange={open => !open && setPrintLot(null)}
      lotId={printLot?.lot_id}
      productName={printLot?.ingredient_name || printLot?.commercial_name || 'Articolo'}
      productType={printLot?.product_type}
      lotNumber={printLot?.lot_number}
    />

  </div></div>;
}
